import type { PurchasingReviewQueueRow } from '@/services/purchasingTypes'
import { formatQboVendorLabel } from './purchasingVendorNames'
import type { SortSpec } from '@/tableControls'
import {
  REVIEW_GROUP_COUNT_SORT_FIELD,
  type ReviewGroupBy,
} from './purchasingReviewTableConfig'

export const REVIEW_GROUP_ROW_HEIGHT = 36
export const REVIEW_ITEM_ROW_HEIGHT = 40

export type ReviewItemGroup = { label: string; items: PurchasingReviewQueueRow[] }

export type ReviewTableRow =
  | { kind: 'group'; label: string; count: number }
  | { kind: 'item'; row: PurchasingReviewQueueRow; groupLabel: string }

export const MULTI_VALUE_GROUP_FIELDS = new Set<ReviewGroupBy>(['reason'])

function emptyLabelForField(field: ReviewGroupBy): string {
  switch (field) {
    case 'vendor':
      return 'No vendor'
    case 'vendor_item_code':
      return 'No item code'
    case 'description':
      return 'No description'
    case 'product':
      return 'No product'
    case 'reason':
      return 'No reason'
    default:
      return 'Not set'
  }
}

function productGroupLabel(row: PurchasingReviewQueueRow): string {
  if (row.meez_ingredient_name) return row.meez_ingredient_name
  if (row.product_name) return row.product_name
  if (row.suggestion?.meez_ingredient_name) return row.suggestion.meez_ingredient_name
  if (row.suggestion?.product_name) return row.suggestion.product_name
  if (row.suggestion?.new_product?.name) return row.suggestion.new_product.name
  return emptyLabelForField('product')
}

function groupLabelsForRow(
  row: PurchasingReviewQueueRow,
  field: ReviewGroupBy,
  vendorNameMap: Record<string, string>,
): string[] {
  if (field === 'reason') {
    const reasons = row.reasons ?? []
    if (reasons.length === 0) return [emptyLabelForField('reason')]
    return reasons
  }

  if (field === 'vendor') {
    const label = formatQboVendorLabel(row.qbo_vendor_id, vendorNameMap)
    return [label || emptyLabelForField('vendor')]
  }

  if (field === 'vendor_item_code') {
    const code = row.vendor_item_code?.trim()
    return [code || emptyLabelForField('vendor_item_code')]
  }

  if (field === 'description') {
    const desc = row.description?.trim()
    return [desc || emptyLabelForField('description')]
  }

  if (field === 'product') {
    return [productGroupLabel(row)]
  }

  return ['Not set']
}

function sortReviewItemGroups(
  groups: ReviewItemGroup[],
  groupBy: ReviewGroupBy,
  groupCountSort: SortSpec | null | undefined,
): ReviewItemGroup[] {
  const emptyLabel = emptyLabelForField(groupBy)
  const sortByCount = groupCountSort?.field === REVIEW_GROUP_COUNT_SORT_FIELD

  if (!sortByCount) {
    const named = groups.filter((group) => group.label !== emptyLabel)
    const emptyGroup = groups.find((group) => group.label === emptyLabel)
    named.sort((a, b) => a.label.localeCompare(b.label))
    return emptyGroup ? [...named, emptyGroup] : named
  }

  const direction = groupCountSort?.direction ?? 'desc'
  const sorted = [...groups].sort((a, b) => {
    const countCmp = a.items.length - b.items.length
    if (countCmp !== 0) {
      return direction === 'asc' ? countCmp : -countCmp
    }
    return a.label.localeCompare(b.label)
  })
  return sorted
}

export function groupReviewRows(
  rows: PurchasingReviewQueueRow[],
  groupBy: ReviewGroupBy,
  vendorNameMap: Record<string, string>,
  groupCountSort?: SortSpec | null,
): ReviewItemGroup[] {
  if (groupBy === 'none') return []

  const buckets = new Map<string, PurchasingReviewQueueRow[]>()

  for (const row of rows) {
    const labels = groupLabelsForRow(row, groupBy, vendorNameMap)
    for (const label of labels) {
      const bucket = buckets.get(label)
      if (bucket) bucket.push(row)
      else buckets.set(label, [row])
    }
  }

  const groups: ReviewItemGroup[] = [...buckets.entries()].map(([label, items]) => ({
    label,
    items,
  }))

  return sortReviewItemGroups(groups, groupBy, groupCountSort)
}

export function flattenReviewGroups(groups: ReviewItemGroup[]): ReviewTableRow[] {
  const flat: ReviewTableRow[] = []
  for (const group of groups) {
    flat.push({ kind: 'group', label: group.label, count: group.items.length })
    for (const row of group.items) {
      flat.push({ kind: 'item', row, groupLabel: group.label })
    }
  }
  return flat
}

export function buildReviewTableRows(
  rows: PurchasingReviewQueueRow[],
  groupBy: ReviewGroupBy,
  vendorNameMap: Record<string, string>,
  groupCountSort?: SortSpec | null,
): ReviewTableRow[] {
  if (groupBy === 'none') {
    return rows.map((row) => ({ kind: 'item', row, groupLabel: '' }))
  }
  return flattenReviewGroups(groupReviewRows(rows, groupBy, vendorNameMap, groupCountSort))
}

export function tableRowKey(groupLabel: string, lineId: number): string {
  return `${groupLabel}\0${lineId}`
}

export type ReviewNavItem = {
  row: PurchasingReviewQueueRow
  groupLabel: string
  tableRowKey: string
}

export function displayNavItems(tableRows: ReviewTableRow[]): ReviewNavItem[] {
  return tableRows
    .filter((entry): entry is Extract<ReviewTableRow, { kind: 'item' }> => entry.kind === 'item')
    .map((entry) => ({
      row: entry.row,
      groupLabel: entry.groupLabel,
      tableRowKey: tableRowKey(entry.groupLabel, entry.row.id),
    }))
}

/** @deprecated Use displayNavItems — kept for tests expecting flat row list */
export function displayOrderItems(tableRows: ReviewTableRow[]): PurchasingReviewQueueRow[] {
  return displayNavItems(tableRows).map((entry) => entry.row)
}

export function buildItemIndexByTableRowKey(tableRows: ReviewTableRow[]): Map<string, number> {
  const map = new Map<string, number>()
  let itemIndex = 0
  for (const entry of tableRows) {
    if (entry.kind === 'item') {
      map.set(tableRowKey(entry.groupLabel, entry.row.id), itemIndex)
      itemIndex += 1
    }
  }
  return map
}

/** Index of the nearest group header at or above `virtualIndex`, or -1 if none. */
export function groupHeaderIndexAtOrBefore(
  tableRows: ReviewTableRow[],
  virtualIndex: number,
): number {
  for (let i = virtualIndex; i >= 0; i -= 1) {
    if (tableRows[i]?.kind === 'group') return i
  }
  return -1
}

export function stickyGroupHeaderAt(
  tableRows: ReviewTableRow[],
  firstVisibleVirtualIndex: number,
): Extract<ReviewTableRow, { kind: 'group' }> | null {
  if (firstVisibleVirtualIndex < 0) return null
  const firstEntry = tableRows[firstVisibleVirtualIndex]
  if (firstEntry?.kind === 'group') return null
  const groupIdx = groupHeaderIndexAtOrBefore(tableRows, firstVisibleVirtualIndex)
  const entry = groupIdx >= 0 ? tableRows[groupIdx] : null
  return entry?.kind === 'group' ? entry : null
}

export function virtualIndexForNavIndex(tableRows: ReviewTableRow[], navIndex: number): number {
  if (navIndex < 0) return -1
  let itemIndex = 0
  for (let virtualIndex = 0; virtualIndex < tableRows.length; virtualIndex += 1) {
    const entry = tableRows[virtualIndex]
    if (entry?.kind === 'item') {
      if (itemIndex === navIndex) return virtualIndex
      itemIndex += 1
    }
  }
  return -1
}
